import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Alert, Space, Typography, Select, Popconfirm, Tag, Grid } from 'antd';
import { PercentageOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const TaxesTab = ({ 
  companies,
  taxes,
  selectedCompany,
  loading,
  onCompanySelect,
  onNew,
  onEdit,
  onDelete,
  onToggleStatus
}) => {
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = useRef(null);
  const screens = useBreakpoint();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, [selectedCompany]);

  const taxColumns = [
    {
      title: 'Código',
      dataIndex: 'tax_code',
      key: 'tax_code',
      width: 100,
    },
    {
      title: 'Nombre',
      dataIndex: 'tax_name',
      key: 'tax_name',
    },
    {
      title: 'Tipo',
      dataIndex: 'tax_type',
      key: 'tax_type',
      render: (type) => (
        <Tag color={type === 'traslado' ? 'blue' : 'orange'}>
          {type === 'traslado' ? 'Traslado' : 'Retención'}
        </Tag>
      ),
    },
    {
      title: 'Factor',
      dataIndex: 'tipo_factor',
      key: 'tipo_factor',
      render: (factor) => (
        <Tag color={factor === 'Tasa' ? 'green' : factor === 'Cuota' ? 'purple' : 'gray'}>
          {factor}
        </Tag>
      ),
    },
    {
      title: 'Tasa/Cuota',
      dataIndex: 'tasa_o_cuota',
      key: 'tasa_o_cuota',
      render: (value, record) => {
        if (record.tipo_factor === 'Tasa') {
          return `${(parseFloat(value) * 100).toFixed(2)}%`;
        }
        return value;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          >
            Editar
          </Button>
          <Button
            type="link"
            icon={record.is_active ? <CloseOutlined /> : <CheckOutlined />}
            onClick={() => onToggleStatus(record)}
          >
            {record.is_active ? 'Desactivar' : 'Activar'}
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este impuesto?"
            description="Esta acción no se puede deshacer."
            onConfirm={() => onDelete(record)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
          Gestión de Impuestos
        </Title>
        
        <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ marginBottom: '16px', width: screens.xs ? '100%' : 'auto' }}>
          <Select
            placeholder="Selecciona una empresa"
            style={{ width: screens.xs ? '100%' : 400 }}
            value={selectedCompany?.id}
            onChange={onCompanySelect}
          >
            {companies.map(company => (
              <Option key={company.id} value={company.id}>
                {company.tax_name} - {company.tax_id}
              </Option>
            ))}
          </Select>
          
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!selectedCompany}
            onClick={onNew}
            block={screens.xs}
          >
            Nuevo Impuesto
          </Button>
        </Space>
      </div>

      <Alert
        message="Información"
        description="Aquí puedes configurar los impuestos específicos para cada empresa. Estos impuestos se utilizarán en la facturación según el catálogo del SAT."
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      {selectedCompany && (
        <>
          <div ref={topPaginationRef} />
          <Table
            columns={taxColumns}
            dataSource={taxes}
            rowKey="id"
            loading={loading}
            locale={{ emptyText: 'No hay impuestos configurados para esta empresa' }}
            pagination={{
              position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}

      {!selectedCompany && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          Selecciona una empresa para ver sus impuestos
        </div>
      )}
    </Card>
  );
};

export default TaxesTab;